import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const statsCardVariants = cva(
  "bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200"
);

export const StatsCard = React.forwardRef(({
  className,
  title,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(statsCardVariants(), className)}
      {...props}
    >
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
});

StatsCard.displayName = "StatsCard";

// Progress Stats Component
export const ProgressStats = React.forwardRef(({
  className,
  completed = 0,
  inProgress = 0,
  completedChange,
  inProgressChange,
  timeframe = "from last week",
  ...props
}, ref) => {
  return (
    <div ref={ref} className={cn("", className)} {...props}>
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Completed */}
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 bg-green-500 rounded-full" />
          <div>
            <div className="text-2xl font-bold text-gray-900">{completed}</div>
            <div className="text-sm text-gray-600">Completed</div>
            {completedChange && (
              <div className="text-xs text-green-600 flex items-center mt-1">
                <i className="fa-solid fa-arrow-up mr-1" />
                {completedChange} {timeframe}
              </div>
            )}
          </div>
        </div>

        {/* In Progress */}
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 bg-yellow-500 rounded-full" />
          <div>
            <div className="text-2xl font-bold text-gray-900">{inProgress}</div>
            <div className="text-sm text-gray-600">In Progress</div>
            {inProgressChange && (
              <div className="text-xs text-red-600 flex items-center mt-1">
                <i className="fa-solid fa-arrow-down mr-1" />
                {inProgressChange} {timeframe}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ProgressStats.displayName = "ProgressStats";

// Productivity Score Component
export const ProductivityScore = React.forwardRef(({
  className,
  score = 0,
  maxScore = 100,
  ...props
}, ref) => {
  const percentage = (score / maxScore) * 100;

  return (
    <div ref={ref} className={cn("mb-6", className)} {...props}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-900">Productivity Score</span>
        <span className="text-sm font-bold text-gray-900">{score}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-primary-600 h-2 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
});

ProductivityScore.displayName = "ProductivityScore";

// Upcoming Deadlines Component
export const UpcomingDeadlines = React.forwardRef(({
  className,
  deadlines = [],
  showViewAll = true,
  onViewAll,
  ...props
}, ref) => {
  return (
    <div ref={ref} className={cn("", className)} {...props}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-900">Upcoming Deadlines</span>
        {showViewAll && (
          <button 
            onClick={onViewAll}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            View all
          </button>
        )}
      </div>
      <div className="space-y-3">
        {deadlines.map((deadline, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                deadline.priority === 'high' ? 'bg-red-500' : 
                deadline.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
              )} />
              <span className="text-sm text-gray-900">{deadline.title}</span>
            </div>
            <span className="text-xs text-gray-600">{deadline.dueDate}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

UpcomingDeadlines.displayName = "UpcomingDeadlines"; 